/**
 * <frame-size-fallback>
 * Wall-art only. The POD (Prodigi) doesn't produce framed prints in the largest
 * sizes, so those Size×Frame variants simply don't exist. Size is the primary
 * axis (shown first, above Frame), so it must always stay selectable: when the
 * customer switches to a size the currently-selected frame isn't available in,
 * silently fall the frame back to the first frame that IS available for that
 * size (in practice "Print Only"). No dead "unavailable" combo is ever reached.
 *
 * The correction runs in the CAPTURE phase, before variant-picker's own (bubble)
 * change handler, so the fixed frame is already checked when the picker reads the
 * checked inputs to build its fetch request. Setting a radio's `.checked`
 * property does not dispatch a change event, so this never triggers a second
 * fetch. The server response then morphs the picker into the final, valid state.
 *
 * Availability comes from an embedded JSON matrix of the Size×Frame combinations
 * that actually exist (rendered by variant-main-picker-wall-art.liquid).
 */
(() => {
  'use strict';

  const SEP = '\u0000';

  class FrameSizeFallback extends HTMLElement {
    connectedCallback() {
      this.exists = this._readMatrix();
      this.picker = this._findPicker();
      if (!this.picker || this.exists.size === 0) return;

      this._onChange = this._onChange.bind(this);
      // Capture phase: run before variant-picker's bubble-phase change handler.
      this.picker.addEventListener('change', this._onChange, true);
    }

    disconnectedCallback() {
      this.picker?.removeEventListener('change', this._onChange, true);
    }

    /** Set of existing combinations, keyed "<size>\0<frame>". */
    _readMatrix() {
      const set = new Set();
      try {
        const rows = JSON.parse(this.querySelector('.frame-size-fallback__data')?.textContent || '[]');
        for (const row of rows) set.add(`${row.s || ''}${SEP}${row.f || ''}`);
      } catch {}
      return set;
    }

    /** The variant picker that owns the options, scoped to this section. */
    _findPicker() {
      const scope = this.closest('.shopify-section') || document;
      return scope.querySelector('variant-picker');
    }

    _frameRadios() {
      return Array.from(this.picker.querySelectorAll('input[type="radio"][data-option-name="Frame"]'));
    }

    /**
     * @param {Event} event
     */
    _onChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.dataset.optionName !== 'Size' || !input.checked) return;

      const size = input.value;
      const frames = this._frameRadios();
      const current = frames.find((radio) => radio.checked);
      if (!current) return;

      // Current frame is produced in the new size — nothing to do.
      if (this.exists.has(`${size}${SEP}${current.value}`)) return;

      // Fall back to the first frame that exists for this size (always Print Only).
      const fallback = frames.find((radio) => this.exists.has(`${size}${SEP}${radio.value}`));
      if (fallback && fallback !== current) fallback.checked = true;
    }
  }

  if (!customElements.get('frame-size-fallback')) {
    customElements.define('frame-size-fallback', FrameSizeFallback);
  }
})();
